import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  GALLERY_BREAKPOINT_TIERS,
  GALLERY_SPLIT_BREAKPOINT_TIERS,
} from 'components/marketplace/helpers/presizedPhotos';
import { Map } from 'components/marketplace/property/Map';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';
import { PropertyPhotoPlaceholder } from 'components/marketplace/shared/PropertyPhoto';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';

import styles from './GalleryPhotos.module.css';

interface GalleryPhotoProps {
  photo: GalleryPhoto;
  idx: number;
  totalPhotos: number;
  address: string;
  toggleLightbox: () => void;
  setSelectedPhotoIndex: (idx: number) => void;
  /** False when an overlay has taken this tile's click, so it must not stay a tab stop with a second action. */
  interactive?: boolean;
}

const GalleryPhotoTile = ({
  photo,
  idx,
  totalPhotos,
  address,
  toggleLightbox,
  setSelectedPhotoIndex,
  interactive = true,
}: GalleryPhotoProps) => {
  const handleClick = () => {
    toggleLightbox();
    setSelectedPhotoIndex(idx);
  };

  const floorPlanLabel = photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : null;
  const alt = floorPlanLabel
    ? `${floorPlanLabel}${address ? ` of ${address}` : ''}`
    : `Photo ${idx + 1} of ${totalPhotos}${address ? ` of ${address}` : ''}`;
  const isSplitTile = idx === 1;
  const className = cn(
    'relative w-full shrink-0 flex-col overflow-hidden bg-od-bg-secondary p-0',
    isSplitTile ? 'h-full' : 'aspect-[3/2]',
  );
  const analyticsName = photo.isFloorPlan
    ? 'cosmos-marketplace-property-desktop-gallery-floor-plan'
    : 'cosmos-marketplace-property-desktop-gallery-photo';
  const contents = (
    <>
      {photo.isFloorPlan ? (
        // Floor plans do not share a consistent aspect ratio; preserve their full geometry.
        <Box className="h-full w-full">
          <GalleryListingPhoto
            photo={photo}
            sizes={isSplitTile ? GALLERY_SPLIT_BREAKPOINT_TIERS : GALLERY_BREAKPOINT_TIERS}
            lazy
            preload={idx === 0}
            fetchPriority={idx === 0 ? 'high' : undefined}
            alt={alt}
            objectFit="contain"
            className="h-full w-full"
          />
        </Box>
      ) : (
        <GalleryListingPhoto
          photo={photo}
          sizes={isSplitTile ? GALLERY_SPLIT_BREAKPOINT_TIERS : GALLERY_BREAKPOINT_TIERS}
          lazy
          preload={idx === 0}
          fetchPriority={idx === 0 ? 'high' : undefined}
          alt={alt}
          objectFit="cover"
          fit={isSplitTile ? 'cover' : undefined}
        />
      )}
      {floorPlanLabel && (
        <Box className="absolute bottom-3 left-3 rounded-lg bg-od-bg-inverse-primary px-3 py-1">
          <Text as="span" className="text-label-sm text-od-content-inverse-primary">
            {floorPlanLabel}
          </Text>
        </Box>
      )}
    </>
  );

  if (!interactive) {
    return (
      <Box analyticsName={analyticsName} className={className}>
        {contents}
      </Box>
    );
  }

  return (
    <Box analyticsName={analyticsName} aria-label={alt} onClick={handleClick} className={className}>
      {contents}
    </Box>
  );
};

interface GalleryPhotosProps {
  property: Pick<
    MarketplaceProperty,
    | 'city'
    | 'floorPlanPhotos'
    | 'latitude'
    | 'listingState'
    | 'longitude'
    | 'photos'
    | 'photosLg'
    | 'photosSm'
    | 'photosXl'
    | 'photosXs'
    | 'state'
    | 'street'
  >;
  toggleLightbox: () => void;
  handleSetSelectedPhotoIndex: (idx: number) => void;
  /** Rendered as the first tile's sibling; the tile is a button, so nested controls would not be operable. */
  heroOverlay?: ReactNode;
}
export const GalleryPhotos = ({
  property,
  toggleLightbox,
  handleSetSelectedPhotoIndex,
  heroOverlay,
}: GalleryPhotosProps) => {
  const galleryPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const totalPhotos = galleryPhotos.length;
  const address = [property.street, property.city, property.state].filter(Boolean).join(', ');

  const Gallery = useMemo(() => {
    const photos = galleryPhotos.map((photo, idx) => {
      // One source for "the overlay owns this tile": the tile surrenders its
      // click and the overlay mounts on the same condition, so the two cannot
      // drift apart.
      const isHeroTile = idx === 0 && Boolean(heroOverlay);
      const galleryPhoto = (
        <GalleryPhotoTile
          key={idx}
          idx={idx}
          interactive={!isHeroTile}
          totalPhotos={totalPhotos}
          address={address}
          photo={photo}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
        />
      );

      if (isHeroTile) {
        return (
          <Box key="hero-photo-overlay" className="relative w-full shrink-0 flex-col">
            {galleryPhoto}
            {heroOverlay}
          </Box>
        );
      }

      if (idx === 1 && property.latitude != null && property.longitude != null) {
        return (
          <Row key="split-photo-map-view" className="w-full justify-between">
            <Box className="aspect-[16/9] h-full w-[49.75%] overflow-hidden">{galleryPhoto}</Box>
            <Box className="aspect-[16/9] w-[49.75%] bg-od-bg-secondary">
              <Map
                key="property-location"
                latitude={property.latitude}
                longitude={property.longitude}
              />
            </Box>
          </Row>
        );
      }

      return galleryPhoto;
    });

    return photos;
  }, [
    galleryPhotos,
    totalPhotos,
    address,
    heroOverlay,
    property.latitude,
    property.longitude,
    toggleLightbox,
    handleSetSelectedPhotoIndex,
  ]);
  const emptyGalleryLocation = streetViewLocationFor({
    availableSoon: property.listingState === 'AVAILABLE_SOON',
    photoCount: totalPhotos,
    latitude: property.latitude,
    longitude: property.longitude,
  });
  const emptyGallery = emptyGalleryLocation ? (
    <StreetViewPhoto
      latitude={emptyGalleryLocation.latitude}
      longitude={emptyGalleryLocation.longitude}
      alt={streetViewAltText(property)}
      className="aspect-[16/9] w-full"
      fallbackLabel="Photos coming soon"
    />
  ) : (
    <PropertyPhotoPlaceholder
      state="unavailable"
      label={property.listingState === 'AVAILABLE_SOON' ? 'Photos coming soon' : undefined}
      className="aspect-[16/9] w-full"
    />
  );

  return (
    <Col
      className={cn(
        styles.hideScrollbar,
        'h-full w-full items-center gap-3 max-md:hidden md:basis-1/2 md:overflow-scroll xl:flex-1',
      )}
    >
      {totalPhotos === 0 ? emptyGallery : Gallery}
    </Col>
  );
};
